import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api, { resolveMediaUrl } from "@/lib/api";
import { Calendar, User, Globe, GraduationCap, Languages, Heart, MapPin, Briefcase, X, Download } from "lucide-react";
import CandidateDocumentsSection from "@/components/CandidateDocumentsSection";

export default function CandidateDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").startsWith("id") ? "id" : "en";
  const [previewLang, setPreviewLang] = useState(lang);
  const [showPreview, setShowPreview] = useState(false);
  const { data: c } = useQuery({ queryKey: ["candidate", id], queryFn: async () => (await api.get(`/candidates/${id}`)).data });
  const { data: schema } = useQuery({ queryKey: ["biodata-schema-public"], queryFn: async () => (await api.get("/biodata-schema")).data });

  // Build a filtered schema map — only fields with `visible !== false` are
  // rendered on the employer detail page (the "PDF" flag controls PDF only).
  const employerFields = React.useMemo(() => {
    const sd = (c && c.schema_data) || {};
    const out = [];
    const seen = new Set();
    (schema?.sections || []).forEach((sec) => {
      if (sec.visible === false) return;
      (sec.fields || []).forEach((fld) => {
        if (fld.visible === false) return;
        const key = fld.key;
        if (!key || seen.has(key)) return;
        const v = sd[key];
        if (v === null || v === undefined || v === "" || v === false) return;
        const label = (fld.label && (fld.label[lang] || fld.label.en)) || key.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
        const display = Array.isArray(v) ? v.join(", ") : (typeof v === "boolean" ? "Yes" : String(v));
        out.push({ key, label, display });
        seen.add(key);
      });
    });
    return out;
  }, [c, schema, lang]);

  if (!c) return <div className="max-w-7xl mx-auto px-6 py-20">Loading…</div>;
  const isSelected = c.status === "Selected";
  const gallery = c.gallery && c.gallery.length > 0 ? c.gallery : [c.photo_url];

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-16">
      <Link to="/catalog" className="text-sm text-zinc-800 hover:text-[#b8860b]">← Back to catalog</Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-10 mt-4 sm:mt-8">
        <div className="lg:col-span-5">
          <div className="rounded-2xl sm:rounded-3xl overflow-hidden border border-zinc-200 aspect-[4/5] max-w-sm mx-auto lg:max-w-none bg-gradient-to-br from-[#d4a017]/20 to-[#b8860b]/35 relative">
            {gallery[0] ? (
              <img
                src={resolveMediaUrl(gallery[0])}
                alt={c.name}
                className="w-full h-full object-cover object-top"
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
              />
            ) : null}
            <div
              className="absolute inset-0 items-center justify-center font-serif text-[#8a5f07] text-7xl"
              style={{ display: gallery[0] ? "none" : "flex" }}
            >{(c.name || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}</div>
          </div>
          {gallery.length > 1 && (
            <div className="grid grid-cols-4 gap-2 mt-3 max-w-sm mx-auto lg:max-w-none">
              {gallery.slice(0, 4).map((g, i) => (
                <img key={g || `gallery-${i}`} src={resolveMediaUrl(g)} alt="" className="aspect-square object-cover object-top rounded-xl border border-zinc-200" />
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-7">
          <div className="mb-2 flex flex-wrap gap-1.5" data-testid="candidate-roles-list">
            {(() => {
              const allRoles = Array.isArray(c.roles) && c.roles.length > 0
                ? c.roles
                : (c.role ? [c.role] : []);
              if (allRoles.length === 0) return <span className="overline">—</span>;
              return allRoles.map((r, i) => (
                <span
                  key={`${r}-${i}`}
                  className={`inline-flex items-center text-[10.5px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-full border ${i === 0 ? "bg-[#b8860b]/15 border-[#b8860b]/40 text-[#8b6508]" : "bg-zinc-50 border-zinc-200 text-zinc-800"}`}
                >
                  {i === 0 && <span className="mr-1" aria-hidden="true">★</span>}{r}
                </span>
              ));
            })()}
          </div>
          <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl text-zinc-950 tracking-tight leading-[1.1]">{c.name}</h1>
          <div className="mt-3 flex flex-wrap gap-x-4 sm:gap-x-6 gap-y-2 text-xs sm:text-sm text-zinc-900">
            <span className="inline-flex items-center gap-1.5"><User size={13} className="text-[#b8860b]" />{c.age} yrs · {c.gender}</span>
            <span className="inline-flex items-center gap-1.5"><Globe size={13} className="text-[#b8860b]" />{c.nationality}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin size={13} className="text-[#b8860b]" />{c.country_name}</span>
          </div>
          <div className="mt-3 inline-block text-xs px-3 py-1 rounded-full border border-zinc-300 bg-zinc-50">Status: {c.status}</div>

          <p className="mt-5 sm:mt-8 text-sm sm:text-base text-zinc-950 leading-relaxed">{c.biodata}</p>

          <div className="mt-6 sm:mt-8 flex flex-wrap gap-2 sm:gap-3">
            {!isSelected ? (
              <Link to={`/candidates/${c.id}/book`} className="btn-gold inline-flex items-center justify-center gap-2 flex-1 sm:flex-none min-w-[9rem]" data-testid="detail-book-btn">
                <Calendar size={16} /> Book Interview
              </Link>
            ) : (
              <div className="btn-gold cursor-not-allowed bg-zinc-900 text-white flex-1 sm:flex-none text-center min-w-[9rem]" data-testid="already-selected">Already Selected</div>
            )}
            <button
              type="button"
              onClick={() => { setPreviewLang(lang); setShowPreview(true); }}
              className="px-4 sm:px-6 py-3 rounded-full border border-zinc-950 text-zinc-950 hover:bg-zinc-950 hover:text-white transition-colors text-sm font-semibold inline-flex items-center justify-center gap-2 flex-1 sm:flex-none min-w-[9rem]"
              data-testid="view-pdf-biodata"
            >
              {t("action.download_biodata", "View PDF Biodata")}
            </button>
            <div className="inline-flex items-center gap-1 text-xs text-zinc-800 w-full sm:w-auto">
              <span>PDF:</span>
              {[
                { code: "en", label: "EN" },
                { code: "id", label: "ID" },
              ].map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => { setPreviewLang(l.code); setShowPreview(true); }}
                  className={`px-2 py-0.5 rounded-full border transition-colors ${
                    (i18n.language || 'en') === l.code
                      ? 'border-[#b8860b] text-[#b8860b]'
                      : 'border-zinc-300 hover:border-zinc-950'
                  }`}
                  data-testid={`pdf-lang-${l.code}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
            <Info icon={Briefcase} label="Experience" value={`${c.experience_years} years`} />
            <Info icon={GraduationCap} label="Education" value={c.education || "—"} />
            <Info icon={Heart} label="Religion" value={c.religion || "—"} />
            <Info icon={User} label="Marital Status" value={`${c.marital_status || "—"}${c.children ? ` · ${c.children} child(ren)` : ""}`} />
          </div>

          <div className="mt-6 sm:mt-8">
            <div className="overline mb-2 sm:mb-3">Skills</div>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {(c.skills || []).map((s) => (
                <span key={s} className="text-xs sm:text-sm px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-zinc-100 border border-zinc-200 text-zinc-800">{s}</span>
              ))}
            </div>
          </div>

          <div className="mt-6 sm:mt-8">
            <div className="overline mb-2 sm:mb-3 inline-flex items-center gap-2"><Languages size={14} /> Languages</div>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {(c.languages || []).map((l) => (
                <span key={l} className="text-xs sm:text-sm px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border border-[#b8860b]/40 text-[#b8860b]">{l}</span>
              ))}
            </div>
          </div>

          {(c.height_cm || c.weight_kg) && (
            <div className="mt-6 sm:mt-8 grid grid-cols-2 gap-3 sm:gap-6">
              <Info icon={User} label="Height" value={c.height_cm ? `${c.height_cm} cm` : "—"} />
              <Info icon={User} label="Weight" value={c.weight_kg ? `${c.weight_kg} kg` : "—"} />
            </div>
          )}

          {/* Work History — bulleted list matching the PDF top-right */}
          {Array.isArray(c.work_history) && c.work_history.length > 0 && (
            <div className="mt-8" data-testid="candidate-work-history">
              <div className="overline mb-3 inline-flex items-center gap-2"><Briefcase size={14} /> Work Experience</div>
              <ul className="space-y-1.5">
                {c.work_history.map((row, i) => (
                  <li key={`${row.country || "?"}-${row.years || 0}-${i}`} className="text-sm text-zinc-800 flex items-start gap-2">
                    <span className="text-[#b8860b] mt-0.5">•</span>
                    <span>
                      {[row.country, row.years ? `${row.years} Year${Number(row.years) === 1 ? "" : "s"}` : null, row.tasks].filter(Boolean).join(", ")}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Additional Information — numbered list matching the PDF bottom-right */}
          {Array.isArray(c.additional_info) && c.additional_info.length > 0 && (
            <div className="mt-8" data-testid="candidate-additional-info">
              <div className="overline mb-3">Additional Information</div>
              <ol className="list-decimal list-inside space-y-1 text-sm text-zinc-800">
                {c.additional_info.slice(0, 5).map((line, i) => (
                  <li key={`ai-${i}`}>{line}</li>
                ))}
              </ol>
            </div>
          )}

          {/* Extra schema-defined fields — filtered by admin's Employer visibility flag */}
          {employerFields.length > 0 && (
            <div className="mt-8" data-testid="candidate-schema-data">
              <div className="overline mb-3">{t("candidate.additional_info", "Additional Information")}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {employerFields.map((f) => (
                  <Info key={f.key} icon={Briefcase} label={f.label} value={f.display} />
                ))}
              </div>
            </div>
          )}

          {/* Documents Vault (view-only) */}
          <div className="mt-8" data-testid="candidate-documents-view">
            <CandidateDocumentsSection candidateId={c.id} readOnly title={t("candidate.documents", "Documents")} />
          </div>
        </div>
      </div>

      {/* Mobile sticky bottom CTA — always-visible for employers on phones */}
      {!isSelected && (
        <div
          className="fixed bottom-0 inset-x-0 z-40 sm:hidden bg-white/95 backdrop-blur-lg border-t border-zinc-200 px-4 py-3 flex items-center gap-2 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
          data-testid="mobile-sticky-cta"
        >
          <a
            href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/candidates/${c.id}/pdf?lang=${i18n.language || 'en'}`}
            target="_blank" rel="noopener noreferrer"
            className="flex-1 text-center text-sm py-3 rounded-full border border-zinc-950 text-zinc-950 font-semibold"
            data-testid="mobile-cta-pdf"
          >
            {t("action.download_biodata", "View PDF")}
          </a>
          <Link
            to={`/candidates/${c.id}/book`}
            className="flex-1 text-center text-sm py-3 rounded-full bg-[#b8860b] text-zinc-950 font-semibold inline-flex items-center justify-center gap-2"
            data-testid="mobile-cta-book"
          >
            <Calendar size={14} /> Book Interview
          </Link>
        </div>
      )}
      {/* Spacer so page content is not hidden behind the sticky bar */}
      {!isSelected && <div className="h-20 sm:hidden" aria-hidden />}

      {/* PDF Preview Modal — embed backend-generated biodata in an iframe. */}
      {showPreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3 sm:p-6"
          data-testid="pdf-preview-modal"
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) setShowPreview(false); }}
        >
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-4xl h-full max-h-[92vh] flex flex-col overflow-hidden">
            <div className="px-4 sm:px-5 py-3 border-b border-zinc-200 flex items-center gap-3 flex-wrap">
              <div className="font-semibold text-sm text-zinc-950 flex-1 truncate">
                {t("candidate.pdf_preview", "PDF Biodata")} · {c.name}
              </div>
              <div className="inline-flex items-center gap-1 text-xs">
                <span className="text-zinc-500">{t("action.change_language", "Language")}:</span>
                {[{ code: "en", label: "EN" }, { code: "id", label: "ID" }].map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => setPreviewLang(l.code)}
                    className={`px-2 py-0.5 rounded-full border transition-colors ${previewLang === l.code ? "border-[#b8860b] text-[#b8860b]" : "border-zinc-300 hover:border-zinc-950"}`}
                    data-testid={`preview-lang-${l.code}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/candidates/${c.id}/pdf/download?lang=${previewLang}`}
                className="text-xs px-3 py-1.5 rounded-full border border-zinc-950 text-zinc-950 hover:bg-zinc-950 hover:text-white inline-flex items-center gap-1.5"
                data-testid="preview-download-btn"
              >
                <Download size={12} /> {t("action.download_pdf", "Download PDF")}
              </a>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                className="p-1.5 rounded-full hover:bg-zinc-100 text-zinc-700"
                data-testid="preview-close-btn"
                aria-label="Close preview"
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 min-h-0 bg-zinc-100">
              <iframe
                key={`preview-${previewLang}`}
                title={`biodata-${c.id}-${previewLang}`}
                src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/candidates/${c.id}/pdf?lang=${previewLang}#toolbar=1&navpanes=0`}
                className="w-full h-full border-0"
                data-testid="pdf-preview-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="border border-zinc-200 rounded-2xl p-4">
      <div className="text-xs text-zinc-800 uppercase tracking-wider flex items-center gap-2 mb-1">
        <Icon size={12} className="text-[#b8860b]" /> {label}
      </div>
      <div className="text-zinc-950">{value}</div>
    </div>
  );
}
